/**
 * Community section - self-loading with its own data fetching
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useCommunityPosts } from '../Hooks/useMotivationData';
import { Theme } from '../../../Theme/Theme';
import { getMediaMedium } from '../../../Model/Media/Media';
import { Translation } from '../../../Components/Translation';

const Section = styled.section``;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-weight: 700;
  font-size: 24px;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};
`;

const SeeAllLink = styled.button`
  background: none;
  border: none;
  color: ${Theme.Colors.primary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: ${Theme.Colors.primary}10;
  }
`;

const PostsMasonry = styled.div`
  column-count: 1;
  column-gap: 16px;

  @media (min-width: 768px) {
    column-count: 2;
  }

  @media (min-width: 992px) {
    column-count: 3;
  }
`;

const PostCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition:
    box-shadow 0.3s ease,
    transform 0.3s ease;
  break-inside: avoid;
  margin-bottom: 16px;

  &:hover {
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
    transform: translateY(-4px);
  }
`;

const PostHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const PostAvatar = styled.div<{ imageUrl?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) => (props.imageUrl ? `url(${props.imageUrl})` : Theme.Colors.primary)};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
`;

const PostAuthor = styled.div`
  flex: 1;
  min-width: 0;
`;

const PostAuthorName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
`;

const PostTime = styled.div`
  font-size: 14px;
  color: ${Theme.Colors.warmGrey};
`;

const PostText = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.textColor};
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PostMedia = styled.div`
  margin-top: 10px;
  border-radius: 10px;
  overflow: hidden;
  aspect-ratio: 16/9;
  background: ${Theme.Colors.lightestGrey};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  color: ${Theme.Colors.warmGrey};
`;

export const CommunitySection: React.FC = observer(() => {
  const { t } = useTrans();
  const navigate = useNavigate();
  const { data: communityPosts } = useCommunityPosts(12);

  const displayPosts = (communityPosts ?? []).slice(0, 6);

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>
          <Translation
            i18nKey="motivation.community"
            defaultValue={`<span style="color: #00dda2">Your</span> community`}
          />
        </SectionTitle>
        {(communityPosts?.length ?? 0) > 6 && (
          <SeeAllLink onClick={() => navigate('/motivation/community')}>
            {t('motivation.seeAll', { defaultValue: 'See all' })}
          </SeeAllLink>
        )}
      </SectionHeader>
      {displayPosts.length > 0 ? (
        <PostsMasonry>
          {displayPosts.map((post) => (
            <PostCard key={post.id}>
              <PostHeader>
                <PostAvatar imageUrl={post.authorProfileImageUri}>
                  {!post.authorProfileImage && (post.authorUsername?.charAt(0) || '?').toUpperCase()}
                </PostAvatar>
                <PostAuthor>
                  <PostAuthorName>
                    {post.authorUsername || t('motivation.anonymous', { defaultValue: 'Anonymous' })}
                  </PostAuthorName>
                  <PostTime>{dayjs(post.createdAt).fromNow()}</PostTime>
                </PostAuthor>
              </PostHeader>
              {post.text && <PostText>{post.text}</PostText>}
              {post.hasMedia && post.medias[0] && (
                <PostMedia>
                  <img src={getMediaMedium(post.medias[0])} alt="" />
                </PostMedia>
              )}
            </PostCard>
          ))}
        </PostsMasonry>
      ) : (
        <EmptyState>
          {t('motivation.noPosts', { defaultValue: 'No community posts yet' })}
        </EmptyState>
      )}
    </Section>
  );
});
